import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCoins, FaCalendarAlt, FaChartLine, FaDollarSign, FaExclamationCircle } from 'react-icons/fa';
import BottomNavigation from './BottomNavigation';
import axios from 'axios';
import { useAuth } from '../security/security/AuthContext';
import HomeToastNotification from './auth/loaders/HomeToastNotification';
import Loading from './auth/loaders/Loading';
import WhatsAppFloat from './auth/WhatsAppFloat';



const OrderItem = ({ order, onReceive }) => {
  const isHighValueProduct = order.investedAmount >= 5000;
  const isReadyToReceive = !isHighValueProduct || 
    (isHighValueProduct && new Date(order.validUntil) <= new Date());
  
  // Add function to determine true expiry (48 hours after validUntil)
  const isTrulyExpired = () => {
    const validUntilDate = new Date(order.validUntil);
    const expiryDate = new Date(validUntilDate);
    expiryDate.setHours(expiryDate.getHours() + 48); // Add 48 hours
    return new Date() > expiryDate;
  };

  // Visual status shows "Expired" after validUntil date, but functionality remains
  const visualStatus = new Date() > new Date(order.validUntil) ? 'expired' : 'active';
  
  // Rest of the calculation functions remain the same
  const calculateProgressPercentage = () => {
    // Existing code unchanged
    const start = new Date(order.createdAt || new Date());
    const end = new Date(order.validUntil);
    const now = new Date();
    
    if (now >= end) return 100;
    
    const total = end.getTime() - start.getTime();
    const progress = now.getTime() - start.getTime();
    return Math.min(100, Math.max(0, (progress / total) * 100));
  };

  return (
    <div className="improved-vision-tech-order-page-item">
      <div className="improved-vision-tech-order-page-card-header">
        <h3 className="improved-vision-tech-order-page-product-name">{order.productName}</h3>
        <span className={`improved-vision-tech-order-page-status ${visualStatus}`}>
          {visualStatus === 'active' ? 'Active' : 'Expired'}
        </span>
      </div>
      
      <div className="improved-vision-tech-order-page-progress-container">
        <div 
          className="improved-vision-tech-order-page-progress-bar" 
          style={{ width: `${calculateProgressPercentage()}%` }}
        ></div>
      </div>
      
      <div className="improved-vision-tech-order-page-details-grid">
        <div className="improved-vision-tech-order-page-detail-item">
          <div className="improved-vision-tech-order-page-detail-icon">
            <FaCalendarAlt />
          </div>
          <div className="improved-vision-tech-order-page-detail-content">
            <span className="improved-vision-tech-order-page-detail-label">Valid Until</span>
            <span className="improved-vision-tech-order-page-detail-value">
              {new Date(order.validUntil).toLocaleDateString('en-US', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              })}
            </span>
          </div>
        </div>
        
        <div className="improved-vision-tech-order-page-detail-item">
          <div className="improved-vision-tech-order-page-detail-icon">
            <FaChartLine />
          </div>
          <div className="improved-vision-tech-order-page-detail-content">
            <span className="improved-vision-tech-order-page-detail-label">Hourly Profit</span>
            <span className="improved-vision-tech-order-page-detail-value">
              KES {(order.dailyIncome / 24).toFixed(2)}
            </span>
          </div>
        </div>
        
        <div className="improved-vision-tech-order-page-detail-item">
          <div className="improved-vision-tech-order-page-detail-icon">
            <FaDollarSign />
          </div>
          <div className="improved-vision-tech-order-page-detail-content">
            <span className="improved-vision-tech-order-page-detail-label">Purchased</span>
            <span className="improved-vision-tech-order-page-detail-value">
              KES {order.investedAmount.toLocaleString()}
            </span>
          </div>
        </div>
        
        <div className="improved-vision-tech-order-page-detail-item">
          <div className="improved-vision-tech-order-page-detail-icon">
            <FaCoins />
          </div>
          <div className="improved-vision-tech-order-page-detail-content">
            <span className="improved-vision-tech-order-page-detail-label">Total Income</span>
            <span className="improved-vision-tech-order-page-detail-value">
              KES {order.totalIncome.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
      
      <div className="improved-vision-tech-order-page-card-footer">
        <div className="improved-vision-tech-order-page-earned-container">
          <span className="improved-vision-tech-order-page-earned-label">Earned Amount</span>
          <span className="improved-vision-tech-order-page-earned-amount">
            +KES {order.earnedAmount.toFixed(2)}
          </span>
        </div>
        
        {!isTrulyExpired() && (
          <button 
            className={`improved-vision-tech-order-page-receive-btn ${!isReadyToReceive || order.earnedAmount <= 0 ? 'disabled' : ''}`}
            onClick={() => onReceive(order.id, order.productType)} 
            disabled={!isReadyToReceive || order.earnedAmount <= 0}
          >
            {!isReadyToReceive
              ? 'Available at End of Cycle' 
              : 'Receive Income'}
            {!isReadyToReceive && (
              <FaExclamationCircle className="improved-vision-tech-order-page-info-icon" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ activeTab }) => (
  <div className="improved-vision-tech-order-page-empty-state">
    <div className="improved-vision-tech-order-page-empty-illustration">
      <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 7V17C4 19.2091 5.79086 21 8 21H16C18.2091 21 20 19.2091 20 17V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M16 3H8C5.79086 3 4 4.79086 4 7H20C20 4.79086 18.2091 3 16 3Z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 11V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M9 14H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
    <h3 className="improved-vision-tech-order-page-empty-title">
      {activeTab === 'normal' 
        ? "No Active Income Shares" 
        : "No Expired Shares Yet"}
    </h3>
    <p className="improved-vision-tech-order-page-empty-message">
      {activeTab === 'normal' 
        ? "Invest in shares to start earning passive income. Your investments will appear here."
        : "Your expired investments will appear here once your active shares complete their cycles."}
    </p>
    {activeTab === 'normal' && (
      <button className="improved-vision-tech-order-page-empty-action-btn">
        Explore Investment Options
      </button>
    )}
  </div>
);

const LoadingState = () => (
  <div className="improved-vision-tech-order-page-loading-state">
    <div className="improved-vision-tech-order-page-loader-spinner">
      <Loading />
    </div>
    <p className="improved-vision-tech-order-page-loading-text">Loading your investments...</p>
  </div>
);

const Order = () => {
  const [activeTab, setActiveTab] = useState('normal');
  const [investments, setInvestments] = useState([]);
  const { userDetails, token, updateUserDetails } = useAuth();
  const [toastNotification, setToastNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUserData = async () => {
    try {
      const userResponse = await axios.get(`https://api.visiontech2030.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
    }
  };

  const refreshAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        refreshUserData(),
        fetchInvestments()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshAllData();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`https://api.visiontech2030.com/api/investments/user/${userDetails.email}`);
      // Sort investments by ID in descending order (higher IDs first)
      const sortedInvestments = response.data.sort((a, b) => b.id - a.id);
      setInvestments(sortedInvestments);
    } catch (error) {
      console.error('Error fetching investments:', error);
      setToastNotification({ 
        message: 'An error occurred while fetching investments. Please try again.', 
        type: 'error' 
      });
    }
  };

  const handleReceive = async (investmentId, productType) => {
    try {
      const investment = investments.find(inv => inv.id === investmentId);
      const isHighValueProduct = investment.investedAmount >= 5000;
      
      if (isHighValueProduct && new Date(investment.validUntil) > new Date()) {
        setToastNotification({ 
          message: 'This investment can only be received at the end of its cycle.', 
          type: 'error' 
        });
        return;
      }

      await axios.post(`https://api.visiontech2030.com/api/investments/${investmentId}/receive`);
      await refreshAllData();
      setToastNotification({ 
        message: 'Income received successfully!', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error receiving income:', error);
      let errorMessage = 'An error occurred while receiving income. Please try again.';
      
      if (error.response && error.response.data) {
        errorMessage = error.response.data;
      }
      
      setToastNotification({ 
        message: errorMessage, 
        type: 'error' 
      });
    }
  };

  const renderInvestments = () => {
    if (isLoading) {
      return <LoadingState />;
    }
  
    // Helper function to determine true expiry (48 hours after validUntil)
    const isTrulyExpired = (investment) => {
      const validUntilDate = new Date(investment.validUntil);
      const expiryDate = new Date(validUntilDate);
      expiryDate.setHours(expiryDate.getHours() + 48); // Add 48 hours
      return new Date() > expiryDate;
    };
  
    // Filter investments based on activeTab and true expiry status
    const filteredInvestments = investments.filter(inv => {
      if (activeTab === 'normal') {
        return !isTrulyExpired(inv); // Show in "Active" tab if not truly expired
      } else {
        return isTrulyExpired(inv); // Show in "Expired" tab only if truly expired (48h after validUntil)
      }
    });
  
    if (filteredInvestments.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }
  
    return filteredInvestments.map(inv => (
      <OrderItem 
        key={inv.id} 
        order={{
          ...inv,
          isActive: !isTrulyExpired(inv), // Mark as active if not truly expired
          validUntil: new Date(inv.validUntil),
          createdAt: inv.createdAt || new Date(new Date(inv.validUntil).getTime() - (90 * 24 * 60 * 60 * 1000)),
        }}
        onReceive={handleReceive}
      />
    ));
  };

  return (
    <div className="improved-vision-tech-order-page-container">
      <div className="improved-vision-tech-order-page-wrapper">
        <div className="improved-vision-tech-order-page-header">
          <h1 className="improved-vision-tech-order-page-title">Income Portfolio</h1>
          
          <div className="improved-vision-tech-order-page-summary-cards">
            <div className="improved-vision-tech-order-page-summary-card">
              <div className="improved-vision-tech-order-page-summary-icon">
                <FaDollarSign />
              </div>
              <div className="improved-vision-tech-order-page-summary-content">
                <span className="improved-vision-tech-order-page-summary-label">Total Invested</span>
                <span className="improved-vision-tech-order-page-summary-value">
                  KES {investments
                    .filter(inv => inv.active)
                    .reduce((total, inv) => total + inv.investedAmount, 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="improved-vision-tech-order-page-summary-card">
              <div className="improved-vision-tech-order-page-summary-icon">
                <FaCoins />
              </div>
              <div className="improved-vision-tech-order-page-summary-content">
                <span className="improved-vision-tech-order-page-summary-label">Available Income</span>
                <span className="improved-vision-tech-order-page-summary-value">
                  KES {investments
                    .filter(inv => inv.active)
                    .reduce((total, inv) => total + inv.earnedAmount, 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="improved-vision-tech-order-page-tabs">
          <button 
            className={`improved-vision-tech-order-page-tab-btn ${activeTab === 'normal' ? 'active' : ''}`}
            onClick={() => setActiveTab('normal')}
          >
            Active Investments
          </button>
          <button 
            className={`improved-vision-tech-order-page-tab-btn ${activeTab === 'expired' ? 'active' : ''}`}
            onClick={() => setActiveTab('expired')}
          >
            Expired
          </button>
        </div>
        
        <div className="improved-vision-tech-order-page-content">
          {renderInvestments()}
        </div>
      </div>
      
      <BottomNavigation />
      {toastNotification && (
        <HomeToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          onClose={() => setToastNotification(null)}
        />
      )}
      <WhatsAppFloat />
      
      <style jsx>{`
        .improved-vision-tech-order-page-container {
          min-height: 100vh;
          background: linear-gradient(150deg, #0f1736 0%, #111c41 100%);
          color: #fff;
          position: relative;
          overflow-x: hidden;
        }
        
        .improved-vision-tech-order-page-container::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 100%;
          height: 300px;
          background: radial-gradient(circle at top right, rgba(103, 76, 215, 0.3), transparent 70%);
          z-index: 0;
          pointer-events: none;
        }
        
        .improved-vision-tech-order-page-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 300px;
          background: radial-gradient(circle at bottom left, rgba(94, 114, 235, 0.15), transparent 70%);
          z-index: 0;
          pointer-events: none;
        }
        
        .improved-vision-tech-order-page-wrapper {
          max-width: 800px;
          margin: 0 auto;
          padding: 24px 16px 100px;
          position: relative;
          z-index: 1;
        }
        
        .improved-vision-tech-order-page-header {
          margin-bottom: 24px;
        }
        
        .improved-vision-tech-order-page-title {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 20px;
          background: linear-gradient(90deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .improved-vision-tech-order-page-summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .improved-vision-tech-order-page-summary-card {
          background: rgba(255, 255, 255, 0.07);
          border-radius: 12px;
          padding: 14px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.3s ease;
          backdrop-filter: blur(5px);
        }
        
        .improved-vision-tech-order-page-summary-card:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
        }
        
        .improved-vision-tech-order-page-summary-icon {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: #fff;
        }
        
        .improved-vision-tech-order-page-summary-content {
          display: flex;
          flex-direction: column;
        }
        
        .improved-vision-tech-order-page-summary-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 2px;
        }
        
        .improved-vision-tech-order-page-summary-value {
          font-size: 16px;
          font-weight: 600;
          color: #fff;
        }
        
        .improved-vision-tech-order-page-tabs {
          display: flex;
          background: rgba(255, 255, 255, 0.04);
          border-radius: 12px;
          padding: 4px;
          margin-bottom: 20px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .improved-vision-tech-order-page-tab-btn {
          flex: 1;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          padding: 12px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .improved-vision-tech-order-page-tab-btn.active {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          box-shadow: 0 4px 12px rgba(126, 58, 242, 0.3);
        }
        
        .improved-vision-tech-order-page-content {
          display: grid;
          gap: 16px;
        }
        
        .improved-vision-tech-order-page-item {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
          position: relative;
        }
        
        .improved-vision-tech-order-page-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          background: rgba(255, 255, 255, 0.08);
        }
        
        .improved-vision-tech-order-page-card-header {
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .improved-vision-tech-order-page-product-name {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #fff;
        }
        
        .improved-vision-tech-order-page-status {
          font-size: 12px;
          font-weight: 500;
          padding: 4px 10px;
          border-radius: 20px;
        }
        
        .improved-vision-tech-order-page-status.active {
          background: rgba(52, 211, 153, 0.2);
          color: #34d399;
        }
        
        .improved-vision-tech-order-page-status.expired {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
        
        .improved-vision-tech-order-page-progress-container {
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          width: 100%;
        }
        
        .improved-vision-tech-order-page-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #4f46e5, #7e3af2);
          transition: width 0.3s ease;
        }
        
        .improved-vision-tech-order-page-details-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 16px;
        }
        
        .improved-vision-tech-order-page-detail-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .improved-vision-tech-order-page-detail-icon {
          background: rgba(255, 255, 255, 0.08);
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .improved-vision-tech-order-page-detail-content {
          display: flex;
          flex-direction: column;
        }
        
        .improved-vision-tech-order-page-detail-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin-bottom: 2px;
        }
        
        .improved-vision-tech-order-page-detail-value {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }
        
        .improved-vision-tech-order-page-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          background: rgba(0, 0, 0, 0.2);
        }
        
        .improved-vision-tech-order-page-earned-container {
          display: flex;
          flex-direction: column;
        }
        
        .improved-vision-tech-order-page-earned-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 2px;
        }
        
        .improved-vision-tech-order-page-earned-amount {
          font-size: 18px;
          font-weight: 600;
          color: #34d399;
        }
        
        .improved-vision-tech-order-page-receive-btn {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border: none;
          color: #fff;
          padding: 10px 20px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .improved-vision-tech-order-page-receive-btn:hover:not(.disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(126, 58, 242, 0.3);
        }
        
        .improved-vision-tech-order-page-receive-btn.disabled {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.5);
          cursor: not-allowed;
        }
        
        .improved-vision-tech-order-page-info-icon {
          font-size: 14px;
          animation: improved-vision-tech-order-page-pulse 2s infinite;
        }
        
        @keyframes improved-vision-tech-order-page-pulse {
          0% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.5;
          }
        }
        
        .improved-vision-tech-order-page-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 40px 20px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 16px;
          border: 1px dashed rgba(255, 255, 255, 0.1);
        }
        
        .improved-vision-tech-order-page-empty-illustration {
          color: rgba(255, 255, 255, 0.2);
          margin-bottom: 16px;
          animation: improved-vision-tech-order-page-float 3s ease-in-out infinite;
        }
        
        @keyframes improved-vision-tech-order-page-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .improved-vision-tech-order-page-empty-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 8px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .improved-vision-tech-order-page-empty-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.5);
          margin: 0 0 20px;
          max-width: 400px;
          line-height: 1.5;
        }
        
        .improved-vision-tech-order-page-empty-action-btn {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border: none;
          color: #fff;
          padding: 10px 20px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .improved-vision-tech-order-page-empty-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(126, 58, 242, 0.3);
        }
        
        .improved-vision-tech-order-page-loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
        }
        
        .improved-vision-tech-order-page-loader-spinner {
          margin-bottom: 16px;
        }
        
        .improved-vision-tech-order-page-loading-text {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        @media (max-width: 768px) {
          .improved-vision-tech-order-page-details-grid {
            grid-template-columns: 1fr;
          }
          
          .improved-vision-tech-order-page-wrapper {
            padding: 16px 12px 100px;
          }
          
          .improved-vision-tech-order-page-title {
            font-size: 24px;
          }
        }
        
        @media (max-width: 480px) {
          .improved-vision-tech-order-page-card-footer {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }
          
          .improved-vision-tech-order-page-receive-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Order;
